import {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import LoaderImage from '@assets/loader.gif';
import { Autocomplete, Box, TextField } from '@mui/material';
import * as am5 from '@amcharts/amcharts5';
import Table from '@components/Table';
import ColumnChart from '@components/Charts/ColumnChart';
/*import LineChart from '@components/Charts/LineChart';*/
import PieChart from '@components/Charts/PieChart';
import FooterChart from '@components/Charts/FooterChart';
import ClusteredColumnChart from '@components/Charts/ClusteredColumnChart';
import { ContentPasteSearch, Stairs, Badge, Close } from '@mui/icons-material';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SupplyDemandSkills from "../../components/SupplyDemandSkills";
import TopSkills from "@components/CountryDetails/components/TopSkills";
import Button from '@components/Button';
import ExportButton from '@components/ExportButton';
import CountryFlag from '../../components/CountryFlag';
import { EducationCareerContext } from '@providers/EducationCareer';
import { OccupationsContext } from '@providers/Occupations';
import { CountriesContext } from '@providers/Countries';
import * as types from '@models/types';
import * as url from '@api/requests';
import { getHubAccess, getFullAccessHubIds } from '@helpers/utils';
import { SkillTypes, WSSkillGroups } from '@helpers/enums';
import './EducationCareerTab.scss';
import Cookies from 'js-cookie';

type EducationCareerTabProps = {
    isLoading: number,
    title: string,
    handleSelectSpecializationsFromChart: (a: types.TopSpecializationsByDemandTypes) => void,
    countryData: types.CountryDataTypes,
    selectedProfile: types.SelectedProfileTypes
};

const topEmployersTableCols = [
    {
        key: 'employer',
        label: 'Top employer',
    },
    {
        key: 'value',
        label: 'Number of job posts',
    }
]

const topOccupationsTableCols = [
    {
        key: 'rank',
        label: '',
    },
    {
        key: 'name',
        label: 'Name',
    },
    {
        key: 'value',
        label: 'Value',
    },
    {
        key: 'percentage',
        label: 'Percentage',
    }
]

const EducationCareerTab: FC<EducationCareerTabProps> = ({ isLoading, title, handleSelectSpecializationsFromChart, countryData, selectedProfile }) => {
    const {
        topEmployersByISCED4,
        //yearlySpecializationAndSupplyTrend,
        //demandAndSupplyPercentageByCountry,
        topSectorAndOccupationByISCED4,
        careerPathwayByISCED4,
        educationLevelPercentage,
        supplyEducationLevelPercentage,
        //totalGraduatesAndStudent,
        //supplyOfGraduatesByISCED4,
        topSpecializationsBySupplyJobPostView,
        topSpecializationsBySupplyGraduatesView,
        topSpecializationsByDemand,
        topSpecializationsByDemandJobPostView,
        topSpecializationsByDemandGraduatesView,
    } = useContext(EducationCareerContext); 
    const { topSkills } = useContext(OccupationsContext);
    const { countryList, countryDetails } = useContext(CountriesContext);
    const [selectedTopSector, setSelectedTopSector] = useState<types.SectorAndOccupationObjTypes>();
    const [loadingSelectedTopSector, setLoadingSelectedTopSector] = useState<boolean>(false);
    const [filteredCountries, setFilteredCountries] = useState<types.CountryType[]>([]);
    const [topSpecializationsByDemandInCountry, setTopSpecializationsByDemandInCountry] = useState<types.TopSpecializationsByDemandTypes[]>([]);
    const [showTopSpecializationsByDemandInCountry, setShowTopSpecializationsByDemandInCountry] = useState<boolean>(false);
    const [loadingTopSpecializationsByDemandInCountry, setLoadingTopSpecializationsByDemandInCountry] = useState<boolean>(false);
    const [selectedCountryForTopSpecializationByDemand, setSelectedCountryForTopSpecializationByDemand] = useState<string | null>(null);
    const [showDemand, setShowDemand] = useState<boolean>(false);
    const [showDemandJobPostView, setShowDemandJobPostView] = useState<boolean>(true);
    const [showSupplyJobPostView, setShowSupplyJobPostView] = useState<boolean>(false);
    const [showTopISCO3OccupationGraphView, setShowTopISCO3OccupationGraphView] = useState<boolean>(true);
    const [showTopISCO4OccupationGraphView, setShowTopISCO4OccupationGraphView] = useState<boolean>(true);
    const [topSkillGroup, setTopSkillGroup] = useState<string>(WSSkillGroups.HardSkill);
    const [skillType, setSkillType] = useState<string>(SkillTypes.ESCO);

    // Reset states on tab change
    useEffect(() => {
        setShowTopSpecializationsByDemandInCountry(false);
        setSelectedCountryForTopSpecializationByDemand(null);
        setShowDemand(false);
        setShowDemandJobPostView(true);
        setShowSupplyJobPostView(false);
        setShowTopISCO3OccupationGraphView(true);
        setShowTopISCO4OccupationGraphView(true);
        setTopSkillGroup(WSSkillGroups.HardSkill);
    }, []);

    const countryDataForTopSpecializationByDemand = useMemo(() => {
        return (
            countryDetails?.find(({ code }: any) => code === selectedCountryForTopSpecializationByDemand) || null
        );
    }, [selectedCountryForTopSpecializationByDemand, countryDetails]);

    const countryCodeForTopSpecializationByDemand = useMemo(
        () => selectedCountryForTopSpecializationByDemand?.toLowerCase() || '',
        [selectedCountryForTopSpecializationByDemand],
    );

    const handleChangeCountryForTopSpecializationByDemand = (event: any, newValue: types.CountryType) => {
        setSelectedCountryForTopSpecializationByDemand && newValue && setSelectedCountryForTopSpecializationByDemand(newValue.countryCode)
    }

    useEffect(() => {
        if (countryList && countryList.length > 0 && filteredCountries.length === 0) {
            var hubAccesses = getHubAccess();
            if (!hubAccesses || (hubAccesses && hubAccesses.length === 0))
                return;

            var tempCountryList = countryList.filter(value => { return hubAccesses.find((h: any) => h.hubId === value.id) });
            setFilteredCountries(tempCountryList);
        }
    }, [countryList, filteredCountries]);

    useEffect(() => {
        async function fetchTopSpecializationsByDemandInCountry() {
            if (countryDataForTopSpecializationByDemand) {
                setLoadingTopSpecializationsByDemandInCountry(true);
                const [errTopSpecializationsByDemandInCountry, incomingTopSpecializationsByDemandInCountry] = await url.getTopSpecializationsByDemand([countryDataForTopSpecializationByDemand.hubId], [], selectedProfile.fromTimeline.title, selectedProfile.toTimeline.title);
                if (errTopSpecializationsByDemandInCountry) console.warn(errTopSpecializationsByDemandInCountry);
                if (setTopSpecializationsByDemandInCountry) setTopSpecializationsByDemandInCountry(incomingTopSpecializationsByDemandInCountry)
                setLoadingTopSpecializationsByDemandInCountry(false);
            }
        }

        fetchTopSpecializationsByDemandInCountry();
    }, [countryDataForTopSpecializationByDemand, selectedProfile.fromTimeline, selectedProfile.toTimeline]);

    useEffect(function () {
        setLoadingSelectedTopSector(true);
        setSelectedTopSector(undefined);
        setLoadingSelectedTopSector(false);
        if (!getFullAccessHubIds().includes(countryData.hubId)) {
            setShowDemand(true);
            setShowDemandJobPostView(false);
        }
        else {
            setShowDemand(false);
            setShowDemandJobPostView(true);
        }
    }, [countryData, selectedProfile])

    const [refreshTopSector, setRefreshTopSector] = useState<boolean>(false);
    const handleSelectTopSector = useCallback((topSector: any) => {
        setLoadingSelectedTopSector(true);
        setSelectedTopSector(topSector);
        setLoadingSelectedTopSector(false);
    }, [setSelectedTopSector]);

    const handleCloseTopOccupation = () => {
        setSelectedTopSector(undefined)
        setRefreshTopSector(true)
        setTimeout(() => setRefreshTopSector(false),100)
    }

    const handleChangeSkillType = useCallback(() => {
        setSkillType((prevSkillsType: string) => (prevSkillsType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS));
    }, []);

    return <div className="occupation-tab">
        {selectedProfile.specializations && selectedProfile.specializations.length !== 0 ? <>
            {isLoading > 0 ? <div className="row mb-3">
                <div className="col-4">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">Top employers for {title}</h4>
                        {topEmployersByISCED4 && topEmployersByISCED4.length ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="topEmployers" urlAPI={url.exportTopEmployersByISCED4} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    isceD4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromTimeline: selectedProfile.fromTimeline?.title,
                                    toTimeline: selectedProfile.toTimeline?.title,
                                    isEntryLevel: selectedProfile.isEntryLevel
                                }} />
                                <Table id="topEmployers" columns={topEmployersTableCols} data={topEmployersByISCED4.map(e => {
                                    e.percentage = typeof (e.percentage) === "number" ? e.percentage + "%" : e.percentage;
                                    return e;
                                })} />
                            </div>
                            :
                            <div className="na">N/A</div>
                        }
                    </div>
                </div>
                <div className="col-8">
                    {/*<div className="widget-component">*/}
                        {/*<h4 className="border-bottom mb-2 pb-2">Historic demand for {title}</h4>*/}
                        {/*{yearlySpecializationAndSupplyTrend && yearlySpecializationAndSupplyTrend.specializationtrend && yearlySpecializationAndSupplyTrend.specializationtrend.length ?*/}
                        {/*    <div>*/}
                        {/*        <ExportButton countryName={countryData.country} exportDOMId="yearlySpecializationAndSupplyTrend-chart-container" urlAPI={url.exportYearlySpecializationAndSupplyTrend} urlDataAPI={{*/}
                        {/*            hubIds: [countryData.hubId],*/}
                        {/*            isceD4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],*/}
                        {/*            locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],*/}
                        {/*            fromQuarter: selectedProfile.fromTimeline?.title,*/}
                        {/*            toQuarter: selectedProfile.toTimeline?.title,*/}
                        {/*            isEntryLevel: selectedProfile.isEntryLevel*/}
                        {/*        }} />*/}
                        {/*        <div id="yearlySpecializationAndSupplyTrend-chart-container">*/}
                        {/*            <LineChart name="yearlySpecializationAndSupplyTrend"*/}
                        {/*                data={yearlySpecializationAndSupplyTrend.specializationtrend.map((e: types.LineChartTypes) => ({ name: e.date, value: e.value }))} selectedQuarter={{ from: selectedProfile.fromTimeline?.title, to: selectedProfile.toTimeline?.title }}*/}
                        {/*                xLabels={{ rotation: -20 }} titleY="Number of job posts" />*/}
                        {/*            <div className="demand-supply-percentage">*/}
                        {/*                {demandAndSupplyPercentageByCountry?.demand_percentage ? <div><b>{demandAndSupplyPercentageByCountry?.demand_percentage}%</b> of jobs in {countryData?.country} demand a degree in {title}</div> : null}*/}
                        {/*                {demandAndSupplyPercentageByCountry?.supply_percentage ? <div><b>{demandAndSupplyPercentageByCountry?.supply_percentage}%</b> of all graduates in {countryData?.country} hold a qualification in {title}</div> : null}*/}
                        {/*            </div>*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*    :*/}
                        {/*    <div className="na">N/A</div>}*/}
                    {/*</div>*/}
                </div>
            </div> : null}


            {isLoading > 1 ? <div className="row mb-3">
                <div className="col-sm-5">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">Demand by education level​</h4>
                        {educationLevelPercentage && educationLevelPercentage.length ?
                            <PieChart name="demandByLevel" data={educationLevelPercentage} categoryFieldName="educationLevel" valueFieldName="percentage" colors={[{ categoryName: "Master", color: am5.color(0x036eaa) }, { categoryName: "Doctorate", color: am5.color(0x035684) }, { categoryName: "Bachelor", color: am5.color(0x003b5c) }, { categoryName: "Diploma", color: am5.color(0x0486cf) }]} />
                            :
                            <div className="na">N/A</div>
                        }
                    </div>
                </div>
                <div className="col-sm-7">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">Supply of graduates by education level​</h4>
                        {supplyEducationLevelPercentage && supplyEducationLevelPercentage.length ?
                            <PieChart name="supplyDemandByLevel" data={supplyEducationLevelPercentage} categoryFieldName="educationLevel" valueFieldName="percentage" colors={[{ categoryName: "Master", color: am5.color(0x036eaa) }, { categoryName: "Doctorate", color: am5.color(0x035684) }, { categoryName: "Bachelor", color: am5.color(0x003b5c) }, { categoryName: "Diploma", color: am5.color(0x0486cf) }]} />
                            :
                            <div className="na">N/A</div>
                        }
                    </div>
                </div>
                {/*<div className="col-sm-12">*/}
                {/*    <div className="widget-component">*/}
                {/*        <h4 className="border-bottom mb-2 pb-2">Supply of students and graduates by institution​​</h4>*/}
                {/*        {totalGraduatesAndStudent && totalGraduatesAndStudent.institutionTypeTotalGraduates && totalGraduatesAndStudent.institutionTypeTotalGraduates.length && totalGraduatesAndStudent.totalNoOfGraduates && totalGraduatesAndStudent.totalNoOfStudents ?*/}
                {/*            <>*/}
                {/*                <div>There are <b>{totalGraduatesAndStudent.totalNoOfGraduates.toLocaleString()}</b> graduates and <b>{totalGraduatesAndStudent.totalNoOfStudents.toLocaleString()}</b> students currently enrolled in {selectedProfile.specializations.length === 1 ? selectedProfile.specializations[0].title : "selected specializations"}</div>*/}
                {/*                <div><b>{totalGraduatesAndStudent.institutionTypeTotalGraduates.find(institutionType => institutionType.institutionType === "HE")?.noOfGraduates.toLocaleString() || 0}</b> are in HE institutions and <b>{totalGraduatesAndStudent.institutionTypeTotalGraduates.find(institutionType => institutionType.institutionType === "TVTC")?.noOfGraduates.toLocaleString() || 0}</b> are in TVTC institutions</div>*/}
                {/*            </>*/}
                {/*            :*/}
                {/*            null*/}
                {/*        }*/}
                {/*        {supplyOfGraduatesByISCED4 && supplyOfGraduatesByISCED4.topFiveSupply && supplyOfGraduatesByISCED4.topFiveSupply.length && supplyOfGraduatesByISCED4.total ?*/}
                {/*            <>*/}
                {/*                <ExportButton countryName={countryData.country} exportDOMId="supplyOfGraduatesByISCED4" urlAPI={url.exportSupplyOfGraduatesByISCED4} urlDataAPI={{*/}
                {/*                    hubIds: [countryData.hubId],*/}
                {/*                    isceD4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],*/}
                {/*                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],*/}
                {/*                    fromTimeline: selectedProfile.fromTimeline?.title,*/}
                {/*                    toTimeline: selectedProfile.toTimeline?.title*/}
                {/*                }} />*/}
                {/*                <div id="supplyOfGraduatesByISCED4">*/}
                {/*                    <div className="total-supply-of-graduates">*/}
                {/*                        <div className="total-supply-of-graduates-headers">*/}
                {/*                            <div className="total-header">No. of Graduates</div>*/}
                {/*                            <div className="total-header">No. of Enrollments</div>*/}
                {/*                            <div className="total-header">No. of Saudi Graduates</div>*/}
                {/*                            <div className="total-header">No. of non-Saudi Graduates</div>*/}
                {/*                            <div className="total-header">No. of Saudis enrolled</div>*/}
                {/*                            <div className="total-header">No. of non-Saudis enrolled</div>*/}
                {/*                        </div>*/}
                {/*                        <div className="total-supply-of-graduates-values">*/}
                {/*                            <div className="total-value">{supplyOfGraduatesByISCED4.total.noOfGraduates.toLocaleString()}</div>*/}
                {/*                            <div className="total-value">{supplyOfGraduatesByISCED4.total.noOfEnrollment.toLocaleString()}</div>*/}
                {/*                            <div className="total-value">{supplyOfGraduatesByISCED4.total.noOfCitizenGraduates.toLocaleString()}</div>*/}
                {/*                            <div className="total-value">{supplyOfGraduatesByISCED4.total.noOfNonCitizenGraduates.toLocaleString()}</div>*/}
                {/*                            <div className="total-value">{supplyOfGraduatesByISCED4.total.noOfCitizenEnrolled.toLocaleString()}</div>*/}
                {/*                            <div className="total-value">{supplyOfGraduatesByISCED4.total.noOfNonCitizenEnrolled.toLocaleString()}</div>*/}
                {/*                        </div>*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            </>*/}
                {/*            : <div className="na">N/A</div>*/}
                {/*        }*/}
                {/*    </div>*/}
                {/*</div>*/}
            </div> : null}

            {isLoading > 2 ? <> {topSectorAndOccupationByISCED4 ? <div className="row mb-3">
                <div className="col-sm-12 mb-3">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">Top sectors for {title} graduates​</h4>
                        {topSectorAndOccupationByISCED4.sectors && topSectorAndOccupationByISCED4.sectors.length ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="sectors-chart" urlAPI={url.exportTopSectorAndOccupationByISCED4} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    ISCED4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromQuarter: selectedProfile.fromTimeline?.title,
                                    toQuarter: selectedProfile.toTimeline?.title,
                                    type: "Sector",
                                    title: title,
                                    isEntryLevel: selectedProfile.isEntryLevel
                                }}
                                />
                                <div id="sectors-chart">
                                    {!refreshTopSector && <ColumnChart name="sectors" data={topSectorAndOccupationByISCED4.sectors} titleX="Sectors " titleY="Number of job posts​" handleSelect={handleSelectTopSector} enableColumnHighlight />}
                                    {!loadingSelectedTopSector && selectedTopSector && selectedTopSector.occupations ?
                                        <div className="position-relative">
                                            <button className="btn position-absolute top-0 end-0 translate-middle" onClick={handleCloseTopOccupation}><Close /></button>
                                            <ColumnChart name="relatedOccupations" data={selectedTopSector.occupations} titleX={"Top Occupations by " + selectedTopSector.name} titleY="Number of job posts​" fillColor={(Cookies.get('darkMode') === "true") ? 0x4e83b1 : 0x8ccbe9} />
                                        </div> :
                                        <></>
                                    }
                                </div>
                            </div> : <div className="na">N/A</div>}
                    </div>
                </div>
                <div className="col-sm-12 mb-3">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top occupations (ISCO 3) for {title}​ graduates
                            {showTopISCO3OccupationGraphView
                                ?
                                <BarChartIcon className="bar-toggle-icon" onClick={() => setShowTopISCO3OccupationGraphView(false)} />
                                :
                                <TableRowsIcon className="bar-toggle-icon" onClick={() => setShowTopISCO3OccupationGraphView(true)} />
                            }
                        </h4>
                        {topSectorAndOccupationByISCED4.isco3_occupations && topSectorAndOccupationByISCED4.isco3_occupations.length ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="isco3occupations-container" urlAPI={url.exportTopSectorAndOccupationByISCED4} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    ISCED4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromQuarter: selectedProfile.fromTimeline?.title,
                                    toQuarter: selectedProfile.toTimeline?.title,
                                    type: "ISCO3Occupation",
                                    title: title,
                                    isEntryLevel: selectedProfile.isEntryLevel
                                }}
                                />
                                <div className="row" id="isco3occupations-container">
                                    {showTopISCO3OccupationGraphView ?
                                        <div id="isco3occupations-chart" className="d-flex align-items-center">
                                            <ColumnChart name="isco3occupations" data={topSectorAndOccupationByISCED4.isco3_occupations.slice(0, 5)} titleX="ISCO3 Occupations" titleY="Number of job posts​​" />
                                        </div>
                                        :
                                        <div id="isco3occupations-table">
                                            <Table id="topISCO3Occupations" columns={topOccupationsTableCols} data={topSectorAndOccupationByISCED4?.isco3_occupations?.map(e => {
                                                if (typeof (e.percentage) === "number") {
                                                    e.name = e.name + " (" + e.iscoCode + ")"
                                                    e.percentage = e.percentage + "%";
                                                }
                                                return e;
                                            })} />
                                        </div>
                                    }
                                </div>
                            </div> : <div className="na">N/A</div>}
                    </div>
                </div>

                <div className="col-sm-12">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top occupations (ISCO 4) for {title}​ graduates
                            {showTopISCO4OccupationGraphView
                                ?
                                <BarChartIcon className="bar-toggle-icon" onClick={() => setShowTopISCO4OccupationGraphView(false)} />
                                :
                                <TableRowsIcon className="bar-toggle-icon" onClick={() => setShowTopISCO4OccupationGraphView(true)} />
                            }
                        </h4>
                        {topSectorAndOccupationByISCED4.isco4_occupations && topSectorAndOccupationByISCED4.isco4_occupations.length ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="isco4occupations-container" urlAPI={url.exportTopSectorAndOccupationByISCED4} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    ISCED4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromQuarter: selectedProfile.fromTimeline?.title,
                                    toQuarter: selectedProfile.toTimeline?.title,
                                    type: "ISCO4Occupation",
                                    title: title,
                                    isEntryLevel: selectedProfile.isEntryLevel
                                }}
                                />
                                <div className="row" id="isco4occupations-container">
                                    {showTopISCO4OccupationGraphView ?
                                        <div id="isco4occupations-chart" className="d-flex align-items-center">
                                            <ColumnChart name="isco4occupations" data={topSectorAndOccupationByISCED4.isco4_occupations.slice(0, 5)} titleX="ISCO4 Occupations" titleY="Number of job posts​​" />
                                        </div>
                                        :
                                        <div id="isco4occupations-table">
                                            <Table id="topISCO4Occupations" columns={topOccupationsTableCols} data={topSectorAndOccupationByISCED4?.isco4_occupations?.map(e => {
                                                if (typeof (e.percentage) === "number") {
                                                    e.name = e.name + " (" + e.iscoCode + ")"
                                                    e.percentage = e.percentage + "%";
                                                }
                                                return e;
                                            })} />
                                        </div>
                                    }
                                </div>
                            </div> : <div className="na">N/A</div>}
                    </div>
                </div>
            </div> : null}
            </> : null}


            {isLoading > 3 ? <div className="mb-3">
                <div className="widget-component">
                    <h4 className="border-bottom mb-2 pb-2">Career pathways with {title}​</h4>
                    {careerPathwayByISCED4 && careerPathwayByISCED4.length ?
                        <div>
                            <ExportButton countryName={countryData.country} exportDOMId="careerPathways" urlAPI={url.exportCareerPathwayByISCED4} urlDataAPI={{
                                hubIds: [countryData.hubId],
                                isceD4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                                locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                fromTimeline: selectedProfile.fromTimeline?.title,
                                toTimeline: selectedProfile.toTimeline?.title,
                                isEntryLevel: selectedProfile.isEntryLevel
                            }} />

                            <div id="careerPathways" className="row row-cols-1 row-cols-md-3 g-4">
                                <div className="col">
                                    <div className="card h-100">
                                        <div>
                                            <h4 className="card-body mb-0 text-center"><ContentPasteSearch /><br />Early Career​</h4>
                                            <ul className="list-group list-group-flush">{
                                                careerPathwayByISCED4.map((e: { iscO3Label: string, career: string }, i: number) => {
                                                    return (e.career === "Early") ? <li className="list-group-item" key={i}>{e.iscO3Label}</li> : null
                                                })
                                            }
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div>
                                        <div className="card h-100">
                                            <h4 className="card-body mb-0 text-center"><Stairs /><br />Mid Career​</h4>
                                            <ul className="list-group list-group-flush">{
                                                careerPathwayByISCED4.map((e: types.CareerPathwayByISCED4Types, i: number) => {
                                                    return (e.career === "Mid") ? <li className="list-group-item" key={i}>{e.iscO3Label}</li> : null
                                                })
                                            }
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div>
                                        <div className="card h-100">
                                            <h4 className="card-body mb-0 text-center"><Badge /><br />Late Career​</h4>
                                            <ul className="list-group list-group-flush">{
                                                careerPathwayByISCED4.map((e: types.CareerPathwayByISCED4Types, i: number) => {
                                                    return (e.career === "Late") ? <li className="list-group-item" key={i}>{e.iscO3Label}</li> : null
                                                })
                                            }
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        : <div className="na">N/A</div>}
                </div>
            </div> : null}

            {isLoading > 3 ? <div className="occupation-tab__supply-demand-skills mb-3">
                <div className="widget-component">
                    <h4 className="border-bottom mb-2 pb-2">Supply Demand Skills</h4>
                    <SupplyDemandSkills countryData={countryData} selectedProfile={selectedProfile} />
                </div>
            </div>: null}
            
            {/* SKILS */}
            {isLoading > 4 ? <div className="occupation-tab__top-skills mb-3">
                <div className="widget-component">
                    <h4 className="border-bottom mb-2 pb-2 occupation-tab__top-skills--header">
                        {skillType === SkillTypes.WS ?
                            <span>Most in-demand {topSkillGroup + "s"} for {title} graduates</span>
                            :
                            <span>Most in-demand ESCOs for {title} graduates</span>
                        }
                        {process.env.REACT_APP_ESCO_ENABLED === "true" &&
                            <Button label={skillType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS} onClick={handleChangeSkillType} style={{ display: 'none' }}  />
                        }
                    </h4>
                    {topSkills && topSkills.length ?
                        <div>
                            <div>
                                {skillType === SkillTypes.WS ?
                                    <>
                                        <HistoryEduIcon className="bar-toggle-icon" id="topSkillsCertification-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.Certification)} />
                                        <AutoFixHighIcon className="bar-toggle-icon" id="topSkillsSoftSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.SoftSkill)} />
                                        <BoltIcon className="bar-toggle-icon" id="topSkillsHardSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.HardSkill)} />
                                    </>
                                    :
                                    <div className="d-flex justify-content-center mt-2">

                                    </div>
                                }
                            </div>
                            <ExportButton countryName={countryData.country} exportDOMId="topSkills" urlAPI={url.exportTopSkills} urlDataAPI={{
                                hubIds: [countryData.hubId],
                                sectorIds: [],
                                occupationCategoryIds: [],
                                isceD4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                                locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                isEntryLevel: selectedProfile.isEntryLevel,
                                type: skillType === SkillTypes.WS ? topSkillGroup : SkillTypes.ESCO,
                            }} />
                            <div id={"topSkills"} >
                                <TopSkills topSkills={topSkills.find(ts => (skillType === SkillTypes.WS ? ts.skillGroup === topSkillGroup : ts.skillGroup === SkillTypes.ESCO))?.topSkills || []} skillType={skillType} />
                            </div>
                        </div>
                        : <div className="na">N/A</div>}
                </div>
            </div> : null}
        </> : <>
            <div className="row supply-demand-skills-container">
                {isLoading > 0 && showDemand && !showDemandJobPostView ? <div className={(showTopSpecializationsByDemandInCountry ? "col-md-6" : "col-md-12") + " mb-3"}>
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top five education specialisations by demand
                            <CompareArrowsIcon className="bar-toggle-icon" id="topSpecializationsByDemand-compare" onClick={() => setShowTopSpecializationsByDemandInCountry(!showTopSpecializationsByDemandInCountry)} />
                        </h4>
                        {topSpecializationsByDemand && topSpecializationsByDemand.length > 0 ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="topSpecializationsByDemand-chart" urlAPI={url.exportTopSpecializationsByDemand} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromTimeline: selectedProfile.fromTimeline?.title,
                                    toTimeline: selectedProfile.toTimeline?.title
                                }} />
                                <div id="topSpecializationsByDemand-chart">
                                    <ClusteredColumnChart chartName="topSpecializationsByDemand" columnName="isced3Label" data={topSpecializationsByDemand} columnsSettings={[{ label: "Entry Level Jobs", fieldName: "countJobPostsForFreshGraduates" }, { label: "Job Posts", fieldName: "countJobPosts" }]} titleX="Specialization" titleY="Number of job posts​​" handleSelect={handleSelectSpecializationsFromChart} hideAxisX />
                                    <FooterChart axisName="Specializations" columnNames={topSpecializationsByDemand.map((value: types.TopSpecializationsByDemandTypes) => { return value.isced3Label })} />
                                </div>
                            </div>
                            :
                            <div className="na">N/A</div>}
                    </div>
                </div> : null}
                {isLoading > 0 && !showDemand && showDemandJobPostView ? <div className={(showTopSpecializationsByDemandInCountry ? "col-md-6" : "col-md-12") + " mb-3"}>
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top five education specialisations by demand job post view
                            <CompareArrowsIcon className="bar-toggle-icon" id="topSpecializationsByDemand-compare" onClick={() => setShowTopSpecializationsByDemandInCountry(!showTopSpecializationsByDemandInCountry)} />
                            <SchoolIcon className="bar-toggle-icon" id="topSpecializationsByDemandGraduatesView-toggle" onClick={() => setShowDemandJobPostView(false)} />
                        </h4>
                        {topSpecializationsByDemandJobPostView && topSpecializationsByDemandJobPostView.length > 0 ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="topSpecializationsByDemandJobPostView-chart" urlAPI={url.exportTopSpecializationsByDemandJobPostView} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromTimeline: selectedProfile.fromTimeline?.title,
                                    toTimeline: selectedProfile.toTimeline?.title
                                }} />
                                <div id="topSpecializationsByDemandJobPostView-chart">
                                    <ClusteredColumnChart chartName="topSpecializationsByDemandJobPostView" columnName="isced3Label" data={topSpecializationsByDemandJobPostView} columnsSettings={[{ label: "Entry Level Jobs", fieldName: "countJobPostsForFreshGraduates" }, { label: "Job Posts", fieldName: "countJobPosts" }]} lineSettings={{ label: "Fresh Graduates", fieldName: "countFreshGraduates" }} titleX="Specialization" titleY="Number of job posts​​" handleSelect={handleSelectSpecializationsFromChart} hideAxisX />
                                    <FooterChart axisName="Specializations" columnNames={topSpecializationsByDemandJobPostView.map((value: types.TopSpecializationsByDemandJobPostViewTypes) => { return value.isced3Label })} />
                                </div>
                            </div>
                            :
                            <div className="na">N/A</div>}
                    </div>
                </div> : null}
                {isLoading > 1 && !showDemand && !showDemandJobPostView ? <div className={(showTopSpecializationsByDemandInCountry ? "col-md-6" : "col-md-12") + " mb-3"}>
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top five education specialisations by demand graduates view
                            <CompareArrowsIcon className="bar-toggle-icon" id="topSpecializationsByDemand-compare" onClick={() => setShowTopSpecializationsByDemandInCountry(!showTopSpecializationsByDemandInCountry)} />
                            <WorkIcon className="bar-toggle-icon" id="topSpecializationsByDemandJobPostView-toggle" onClick={() => setShowDemandJobPostView(true)} />
                        </h4>
                        {topSpecializationsByDemandGraduatesView && topSpecializationsByDemandGraduatesView.length > 0 ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="topSpecializationsByDemandGraduatesView-chart" urlAPI={url.exportTopSpecializationsByDemandGraduatesView} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromTimeline: selectedProfile.fromTimeline?.title,
                                    toTimeline: selectedProfile.toTimeline?.title
                                }} />
                                <div id="topSpecializationsByDemandGraduatesView-chart">
                                    <ClusteredColumnChart chartName="topSpecializationsByDemandGraduatesView" columnName="isced3Label" data={topSpecializationsByDemandGraduatesView} columnsSettings={[{ label: "Fresh Graduates", fieldName: "countFreshGraduates" }, { label: "Students", fieldName: "countStudents" }]} lineSettings={{ label: "Entry Level Jobs", fieldName: "countJobPostsForFreshGraduates" }} titleX="Specialization" titleY="Number of students and fresh graduates​​" handleSelect={handleSelectSpecializationsFromChart} hideAxisX height={320} />
                                    <FooterChart axisName="Specializations" columnNames={topSpecializationsByDemandGraduatesView.map((value: types.TopSpecializationsByDemandGraduatesViewTypes) => { return value.isced3Label })} />
                                </div>
                            </div>
                            :
                            <div className="na">N/A</div>}
                    </div>
                </div> : null}
                {isLoading > 1 && showTopSpecializationsByDemandInCountry ? <div className="col-md-6 mb-3">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">Top five education specialisations by demand in country {countryDataForTopSpecializationByDemand && countryDataForTopSpecializationByDemand.country}​</h4>
                        {!loadingTopSpecializationsByDemandInCountry ?
                            <>
                                {topSpecializationsByDemandInCountry && topSpecializationsByDemandInCountry.length > 0 ?
                                    <ExportButton countryName={countryDataForTopSpecializationByDemand.country} exportDOMId="topSpecializationsByDemandInCountry-chart" urlAPI={url.exportTopSpecializationsByDemand} urlDataAPI={{
                                        hubIds: [countryDataForTopSpecializationByDemand.hubId],
                                        locations: [],
                                        fromTimeline: selectedProfile.fromTimeline?.title,
                                        toTimeline: selectedProfile.toTimeline?.title
                                    }} />
                                    :
                                    <></>
                                }
                                <div id="topSpecializationsByDemandInCountry-chart">
                                    <div className="top-specialization-by-demand-in-country-container">
                                        <CountryFlag countryCode={countryCodeForTopSpecializationByDemand} />
                                        {countryList && countryList.length ? <Autocomplete
                                            id="countriesDDLForTopSpecializationByDemand"
                                            value={countryDataForTopSpecializationByDemand}
                                            options={countryList.filter(value => { return value.hubStatus === "Activated" })}
                                            autoHighlight fullWidth
                                            onChange={handleChangeCountryForTopSpecializationByDemand}
                                            getOptionLabel={(option) => option.country}
                                            isOptionEqualToValue={(option, value) => option.country === value.country}
                                            renderOption={(props, option) => (
                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                    {option.country} ({option.countryCode})
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="standard"
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        autoComplete: 'new-password', // disable autocomplete and autofill
                                                        placeholder: "Choose Country"
                                                    }}
                                                />
                                            )}
                                        /> : null}
                                    </div>
                                    {topSpecializationsByDemandInCountry && topSpecializationsByDemandInCountry.length > 0 ?
                                        <>
                                            <ClusteredColumnChart chartName="topSpecializationsByDemandInCountry" columnName="isced3Label" data={topSpecializationsByDemandInCountry} columnsSettings={[{ label: "Entry Level Jobs", fieldName: "countJobPostsForFreshGraduates" }, { label: "Job Posts", fieldName: "countJobPosts" }]} titleX="specialization" titleY="Number of job posts​​" handleSelect={handleSelectSpecializationsFromChart} hideAxisX />
                                            <FooterChart axisName="Specializations" columnNames={topSpecializationsByDemandInCountry.map((value: types.TopSpecializationsByDemandTypes) => { return value.isced3Label })} />
                                        </>
                                        :
                                        <></>
                                    }
                                    {!topSpecializationsByDemandInCountry || (topSpecializationsByDemandInCountry && topSpecializationsByDemandInCountry.length === 0) ?
                                        <div className="na">N/A</div>
                                        :
                                        <></>
                                    }
                                </div>
                            </>
                            :
                            <div id="topSpecializationsByDemandInCountry-chart-loading"><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div>
                        }
                    </div>
                </div> : null}
                    
                {isLoading > 2 && showSupplyJobPostView ? <div className="col-md-12 mb-3">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top five education specialisations by supply job post view
                            <SchoolIcon className="bar-toggle-icon" id="topSpecializationsBySupplyGraduatesView-toggle" onClick={() => setShowSupplyJobPostView(false)} />
                        </h4>
                        {topSpecializationsBySupplyJobPostView && topSpecializationsBySupplyJobPostView.length > 0 ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="topSpecializationsBySupplyJobPostView-chart" urlAPI={url.exportTopSpecializationsBySupplyJobPostView} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromTimeline: selectedProfile.fromTimeline?.title,
                                    toTimeline: selectedProfile.toTimeline?.title
                                }} />
                                <div id="topfiveSpecializationsJobPostView-chart">
                                    <ClusteredColumnChart chartName="topSpecializationsBySupplyJobPostView" columnName="isced3Label" data={topSpecializationsBySupplyJobPostView} columnsSettings={[{ label: "Entry Level Jobs", fieldName: "countJobPostsForFreshGraduates" }, { label: "Job Posts", fieldName: "countJobPosts" }]} lineSettings={{ label: "Fresh Graduates", fieldName: "countFreshGraduates" }} titleX="Specialization" titleY="Number of job posts​​" handleSelect={handleSelectSpecializationsFromChart} hideAxisX />
                                    <FooterChart axisName="Specializations" columnNames={topSpecializationsBySupplyJobPostView.map((value: types.TopSpecializationsBySupplyJobPostViewTypes) => { return value.isced3Label })} />
                                </div>
                            </div>
                            :
                            <div className="na">N/A</div>}
                    </div>
                </div> : null}
                {isLoading > 3 && !showSupplyJobPostView ? <div className="col-md-12 mb-3">
                    <div className="widget-component">
                        <h4 className="border-bottom mb-2 pb-2">
                            Top five education specialisations by supply graduates view
                            <WorkIcon className="bar-toggle-icon" id="topSpecializationsBySupplyJobPostView-toggle" onClick={() => setShowSupplyJobPostView(true)} />
                        </h4>
                        {topSpecializationsBySupplyGraduatesView && topSpecializationsBySupplyGraduatesView.length > 0 ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="topSpecializationsBySupplyGraduatesView-chart" urlAPI={url.exportTopSpecializationsBySupplyGraduatesView} urlDataAPI={{
                                    hubIds: [countryData.hubId],
                                    locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                    fromTimeline: selectedProfile.fromTimeline?.title,
                                    toTimeline: selectedProfile.toTimeline?.title
                                }} />
                                <div id="topfiveSpecializationsGraduatesView-chart">
                                    <ClusteredColumnChart chartName="topSpecializationsBySupplyGraduatesView" columnName="isced3Label" data={topSpecializationsBySupplyGraduatesView} columnsSettings={[{ label: "Fresh Graduates", fieldName: "countFreshGraduates" }, { label: "Students", fieldName: "countStudents" }]} lineSettings={{ label: "Entry Level Jobs", fieldName: "countJobPostsForFreshGraduates" }} titleX="Specialization" titleY="Number of students and fresh graduates​​" handleSelect={handleSelectSpecializationsFromChart} hideAxisX height={320} />
                                    <FooterChart axisName="Specializations" columnNames={topSpecializationsBySupplyGraduatesView.map((value: types.TopSpecializationsBySupplyGraduatesViewTypes) => { return value.isced3Label })} />
                                </div>
                            </div>
                            :
                            <div className="na">N/A</div>}
                    </div>
                </div> : null}
            </div>
        </>
        }
        {isLoading !== 5 ? <div><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div> : null}
    </div>;
};

export default EducationCareerTab;
